import { useEffect, useState } from 'react';
import type { ReactNode, SyntheticEvent } from 'react';

import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import { Box, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useRetailEvotorAccounts } from '../../hooks';
import { useDomainPermissions, useFeedback } from '../../lib';
import { RETAIL_TABS, isRetailTab } from '../../model/retailNav';
import type { RetailTab } from '../../model/retailNav';
import {
  FeedbackSnackbar,
  RetailEvotorAccountsPanel,
  RetailEvotorAutomationPanel,
  RetailEvotorDevicesPanel,
  RetailEvotorOverviewPanel,
  RetailEvotorStoresPanel,
  RetailEvotorSyncPanel,
  SettingsScaffold,
} from '../components';

interface RetailTabPanelProps {
  tab: RetailTab;
  active: RetailTab;
  children: ReactNode;
}

/** Fully unmounts an inactive tab's content (not just CSS-hidden) — every panel below owns its
 * own query/state, so switching tabs should stop that work rather than leave it running hidden. */
function RetailTabPanel({ tab, active, children }: RetailTabPanelProps) {
  return (
    <Box
      role="tabpanel"
      id={`retail-tabpanel-${tab}`}
      aria-labelledby={`retail-tab-${tab}`}
      hidden={tab !== active}
    >
      {tab === active ? children : null}
    </Box>
  );
}

/**
 * "Ритейл" — the single Evotor cloud-POS integration settings screen, covering the merchant's
 * whole journey in order: подключаем аккаунты → загружаем данные → делаем маппинги → выгружаем
 * обновлённые данные → включаем регулярные интеграции → смотрим состояние. Six tabs inside one
 * `SettingsScaffold` (`?tab=` query state, deep-linkable), `overview` first and default.
 *
 * `SettingsScaffold` is used here **without** a `query` — this page has no single primary
 * resource to gate on (five of the six tabs each own an independent query, and Синхронизация has
 * none at all: its rows are stateless triggers). An earlier revision worked around
 * `SettingsScaffold`'s formerly-mandatory `query` prop with a synthetic always-resolved
 * `useQuery` stub just to satisfy the type; `SettingsScaffold.query` is now genuinely optional
 * (see its own doc comment), so that stub is gone — `canView`/`canManage` still gate the whole
 * screen exactly as before, and every tab fully owns its own loading/error/no-access presentation.
 *
 * No page-level save bar (`onSave` omitted) — the only tab with a save/draft flow is Автоматизация,
 * which is gated on a *different* permission namespace (`integrations.evotor.*`, not
 * `settings.retail-evotor.*`) and so owns its own compact save bar scoped to just that tab.
 */
export function RetailSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage, canManageCredentials } = useDomainPermissions('retail-evotor');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: RetailTab = isRetailTab(tabParam) ? tabParam : 'overview';

  const [accountsFilterInput, setAccountsFilterInput] = useState('');
  const [accountsFilter, setAccountsFilter] = useState('');
  const [accountsPage, setAccountsPage] = useState(0);
  const [accountsSize, setAccountsSize] = useState(10);

  // Debounce the free-text account name search, mirroring `ProductsPage`'s own debounce pattern.
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setAccountsFilter(accountsFilterInput.trim());
      setAccountsPage(0);
    }, 400);
    return () => window.clearTimeout(handle);
  }, [accountsFilterInput]);

  const accountsQuery = useRetailEvotorAccounts({
    name: accountsFilter || undefined,
    page: accountsPage,
    size: accountsSize,
    sort: 'createdAt:desc',
  });

  const { feedback, success, error: notifyError, clear } = useFeedback();

  const goToTab = (next: RetailTab) => setSearchParams({ tab: next }, { replace: true });
  const handleTabChange = (_event: SyntheticEvent, next: RetailTab) => goToTab(next);

  return (
    <SettingsScaffold
      title={t('retailSettings.title')}
      subtitle={t('retailSettings.subtitle')}
      icon={<PointOfSaleRoundedIcon />}
      canView={canView}
      canManage={canManage}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      <Box>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label={t('retailSettings.tabsAriaLabel')}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mb: 3 }}
        >
          {RETAIL_TABS.map((value) => (
            <Tab
              key={value}
              value={value}
              id={`retail-tab-${value}`}
              aria-controls={`retail-tabpanel-${value}`}
              label={t(`retailSettings.tabs.${value}`)}
            />
          ))}
        </Tabs>

        <RetailTabPanel tab="overview" active={tab}>
          <RetailEvotorOverviewPanel onNavigateToTab={goToTab} />
        </RetailTabPanel>

        <RetailTabPanel tab="accounts" active={tab}>
          <RetailEvotorAccountsPanel
            query={accountsQuery}
            filterInput={accountsFilterInput}
            onFilterInputChange={setAccountsFilterInput}
            page={accountsPage}
            size={accountsSize}
            onPageChange={setAccountsPage}
            onSizeChange={setAccountsSize}
            canManageCredentials={canManageCredentials}
            onSuccess={success}
            onError={notifyError}
          />
        </RetailTabPanel>

        <RetailTabPanel tab="stores" active={tab}>
          <RetailEvotorStoresPanel
            canManage={canManage}
            onGoToAccountsTab={() => goToTab('accounts')}
            onSuccess={success}
            onError={notifyError}
          />
        </RetailTabPanel>

        <RetailTabPanel tab="devices" active={tab}>
          <RetailEvotorDevicesPanel canManage={canManage} onSuccess={success} onError={notifyError} />
        </RetailTabPanel>

        <RetailTabPanel tab="sync" active={tab}>
          <RetailEvotorSyncPanel canManage={canManage} />
        </RetailTabPanel>

        <RetailTabPanel tab="automation" active={tab}>
          <RetailEvotorAutomationPanel active={tab === 'automation'} />
        </RetailTabPanel>
      </Box>
    </SettingsScaffold>
  );
}
